// ============================================================
// EncartPrevaut — CE QUI PRÉVAUT SUR L'ESTIMATION.
// ------------------------------------------------------------
// « Laissé vide, le programme calcule. Rempli, c'est votre valeur qui
// part sur l'offre. » Quatre champs, une seule règle, écrite au-dessus
// d'eux — §6.
//
// Extrait de `PieceMaterial.jsx` le 11 septembre 2026, quand il a fallu
// dire pourquoi le coefficient matière peut être sans effet. Le fichier
// était à son plafond du §4 ; ce bloc est autonome et déjà rendu à part
// par `PieceEditor`.
// ============================================================

import NumberField from '../../ui/NumberField.jsx';
import { formatEuroOuTiret, formatNumber } from '../../lib/format.js';

/**
 * CE QUI PRÉVAUT sur l'estimation — §1.2, refait le 11 août 2026.
 *
 * ── UNE SEULE RÈGLE, ET ELLE EST ÉCRITE AU-DESSUS DES CHAMPS ──
 *
 *   vide   → on garde la valeur calculée
 *   rempli → c'est la vôtre qui gagne
 *
 * Le bouton « Retenir » a disparu avec l'idée de « figer ». Il fallait
 * un geste pour qu'une valeur cesse de suivre le calcul, et ce geste
 * n'était nulle part sur le devis : on ne pouvait pas savoir, en lisant
 * l'écran, si un montant était suivi ou figé. Maintenant si — un champ
 * rempli est une décision, un champ vide n'en est pas une.
 *
 * ── POURQUOI LA MARGE REVIENT SANS ÊTRE LA MARGE SUPPRIMÉE ──
 *
 * Celle du 5 août portait sur l'USINAGE, dans les paramètres, et devait
 * rester à 1 : le taux de 80 €/h est déjà un prix de vente. Celles-ci
 * portent sur ce que Mertens ACHÈTE ET REVEND — la matière, la
 * sous-traitance — où une marge de revente est légitime. Elles
 * existaient déjà, cachées dans les paramètres à 1,10 et 1,15 ; elles
 * changent de PLACE, pas de nature.
 */
export function EncartPrevaut({ piece, onChange, disabled, mat, detail }) {
  const fournie = piece.matiere_fournie_client === true;
  // ── UN MONTANT RETENU REND LE COEFFICIENT SANS EFFET ──
  //
  // Charles, 11 septembre 2026 : « le prix total ne se met pas à jour
  // quand on change le coefficient matière ». Le calcul avait RAISON :
  // sa pièce porte 2 100 € d'achat matière retenus à la main, et le §6
  // dit qu'un montant retenu part TEL QUEL — « lui appliquer le
  // coefficient reviendrait à majorer un nombre que quelqu'un a déjà
  // arrêté ». C'est l'ÉCRAN qui se taisait : champ actif, libellé « Le
  // vôtre, sur cette pièce », et rien qui bouge.
  //
  // §11, 22e ligne : un champ qui ne peut rien faire invite au geste qui
  // ne fait rien. On le grise, et on nomme le champ à vider.
  const montantRetenu = piece.montant_matiere_retenu != null
    && piece.montant_matiere_retenu !== '';

  return (
      <div className="encart">
        <p className="caps">Ce qui prévaut sur l’estimation</p>
        <p className="field__hint">
          <strong>Laissé vide, le programme calcule. Rempli, c’est votre valeur qui part sur
          l’offre.</strong> Rien à valider, rien à figer.
        </p>

        <div className="grid-2">
          <NumberField
            label="Prix d’achat matière"
            unit="€"
            value={piece.montant_matiere_retenu}
            min={0}
            disabled={disabled || fournie}
            hint={
              piece.montant_matiere_retenu == null
                ? `Vide : le programme retient ${formatEuroOuTiret(mat?.cost_calcule ?? null)}`
                : 'Votre montant, tel quel. Il ne suit plus le prix au kilo.'
            }
            onChange={(v) => onChange({ montant_matiere_retenu: v })}
          />
          {/* Le TRANSPORT. Pré-rempli à 0 : « pas de transport facturé »
              est une réponse vraie et c'est la réponse normale — un zéro
              DÉCIDÉ, pas une absence déguisée. */}
          <NumberField
            label="Transport matière"
            unit="€"
            value={piece.transport_matiere ?? 0}
            min={0}
            disabled={disabled}
            hint="Rebillé au coût. Sort en ligne séparée, hors engagement."
            onChange={(v) => onChange({ transport_matiere: v ?? 0 })}
          />
          {/* ── LES DEUX COEFFICIENTS DE REVENTE ──
              Ils ne portent JAMAIS sur l'usinage : le taux horaire est
              déjà un prix de vente, et une marge par-dessus le
              compterait deux fois. C'est la raison pour laquelle la
              marge globale a été supprimée le 5 août, et elle reste
              valable. */}
          <NumberField
            label="Coefficient matière"
            value={piece.coefficient_matiere}
            min={0}
            disabled={disabled || fournie || montantRetenu}
            hint={
              fournie
                ? 'Sans effet : la matière est fournie par le client.'
                : montantRetenu
                  ? 'Sans effet : votre prix d’achat matière part tel quel sur l’offre. '
                    + 'Videz-le pour que le coefficient reprenne la main.'
                  : detail?.coefficient_matiere_source === 'piece'
                    ? 'Le vôtre, sur cette pièce.'
                    : `Vide : celui des paramètres (${detail?.coefficient_matiere != null
                        ? formatNumber(detail.coefficient_matiere) : '—'})`
            }
            onChange={(v) => onChange({ coefficient_matiere: v })}
          />
          {/* ── LE COEFFICIENT TRAITEMENT A QUITTÉ CETTE PLACE ──
              21 août 2026, Charles : « le coefficient traitement n'a rien
              à faire dans la partie matière, chaque traitement ajouté en
              bas doit avoir son coefficient, point. »

              Il avait raison au-delà de la place du champ : le coefficient
              s'appliquait DEUX FOIS — une fois sur la ligne
              (`purchase_coef`), une fois ici sur la somme. Un zingage à
              100 € partait sur le devis à 138 €.

              Il vit désormais sur la ligne de sous-traitance, et nulle
              part ailleurs. Voir `shared/pricing.js`, formule v5. */}
        </div>

        {/* « Matière fournie par le client » est montée en tête du bloc
            Matière (6 octobre 2026) : une seule porte par valeur. */}
      </div>
  );
}

export default EncartPrevaut;
